import { useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { GWPField, GWPInput, GWPMultiSelect, GWPSelect, GWPSegmentedControl } from 'shared-components';
import ApplicationSection from '../ApplicationSection';
import {
	EntertainerApplicationContract,
	EntertainerApplicationFormState,
	PerformerRangeMode,
} from '../EntertainerApplicationTypes';
import { fieldError, useApplicationFormUpdate } from '../EntertainerApplicationFormUtils';

type Props = { contract: EntertainerApplicationContract };
type MultiSelectMenu = 'act-formats' | 'genres' | 'specialties';

export default function ActSection({ contract }: Props) {
	const { control, clearErrors, formState } = useFormContext<EntertainerApplicationFormState>();
	const update = useApplicationFormUpdate();
	const [openMultiSelect, setOpenMultiSelect] = useState<MultiSelectMenu | null>(null);
	const [mode, name, minimum, maximum, formats, genres, specialties] = useWatch({
		control,
		name: [
			'performerRangeMode',
			'EntertainerName',
			'EntertainerMinimumPerformerCount',
			'EntertainerMaximumPerformerCount',
			'EntertainerActFormats',
			'EntertainerGenres',
			'EntertainerSpecialties',
		],
	});
	const errors = formState.errors;
	const genresRequired = contract.constraints.genres.requiredForActFormats.some((format) => formats.includes(format));
	const performerOptions = useMemo(() => {
		const { minimum: first, maximum: last } = contract.constraints.performerCount;
		return Array.from({ length: last - first + 1 }, (_, index) => {
			const count = first + index;
			return {
				value: String(count),
				label: count === 1 ? '1 — Solo' : count === last ? `${count}+` : String(count),
			};
		});
	}, [contract.constraints.performerCount]);

	const selectMode = (nextMode: PerformerRangeMode) => {
		update('performerRangeMode', nextMode);
		clearErrors(['EntertainerMinimumPerformerCount', 'EntertainerMaximumPerformerCount']);
		if (nextMode === 'one-size') {
			update('EntertainerMaximumPerformerCount', minimum);
			return;
		}
		if (Number(minimum) === contract.constraints.performerCount.maximum) {
			update('EntertainerMinimumPerformerCount', '');
		}
		update('EntertainerMaximumPerformerCount', '');
	};

	return (
		<ApplicationSection
			title="Your act"
			description="The essentials our entertainment team uses to understand your setup."
		>
			<div className="entertainer-grid act-details-grid">
				<GWPField label="Name of act" required error={fieldError(errors, 'EntertainerName')}>
					<GWPInput
						id="EntertainerName"
						value={name}
						onValueChange={(value) => update('EntertainerName', value)}
						autoComplete="organization"
					/>
				</GWPField>
				<div className="performer-range-control">
					<GWPField label="Act configuration">
						<GWPSegmentedControl
							aria-label="Act configuration"
							options={[
								{ value: 'one-size', label: 'One size' },
								{ value: 'flexible-range', label: 'Flexible range' },
							]}
							value={mode}
							onValueChange={selectMode}
							size="sm"
							tone="primary"
							fullWidth
						/>
					</GWPField>
					{mode === 'one-size' ? (
						<GWPField
							label="Performers"
							required
							error={
								fieldError(errors, 'EntertainerMinimumPerformerCount') ??
								fieldError(errors, 'EntertainerMaximumPerformerCount')
							}
						>
							<GWPSelect
								id="EntertainerMinimumPerformerCount"
								value={minimum || null}
								placeholder="Select act size"
								options={performerOptions}
								onValueChange={(value) => {
									update('EntertainerMinimumPerformerCount', value ?? '');
									update('EntertainerMaximumPerformerCount', value ?? '');
								}}
							/>
						</GWPField>
					) : (
						<div className="performer-range-group">
							<GWPField
								label="Smallest lineup"
								required
								error={fieldError(errors, 'EntertainerMinimumPerformerCount')}
							>
								<GWPSelect
									id="EntertainerMinimumPerformerCount"
									value={minimum || null}
									placeholder="Select minimum"
									options={performerOptions.slice(0, -1)}
									onValueChange={(value) => {
										update('EntertainerMinimumPerformerCount', value ?? '');
										if (value === null || Number(maximum) <= Number(value)) {
											update('EntertainerMaximumPerformerCount', '');
										}
									}}
								/>
							</GWPField>
							<GWPField
								label="Largest lineup"
								required
								error={fieldError(errors, 'EntertainerMaximumPerformerCount')}
							>
								<GWPSelect
									id="EntertainerMaximumPerformerCount"
									value={maximum || null}
									placeholder={minimum ? 'Select maximum' : 'Select minimum first'}
									options={performerOptions.filter(
										(option) => Number(option.value) > Number(minimum),
									)}
									disabled={!minimum}
									onValueChange={(value) => update('EntertainerMaximumPerformerCount', value ?? '')}
								/>
							</GWPField>
						</div>
					)}
				</div>
			</div>
			<GWPField
				label="Act formats"
				required
				hint={`Select ${contract.constraints.actFormats.minSelections} to ${contract.constraints.actFormats.maxSelections}`}
				error={fieldError(errors, 'EntertainerActFormats')}
			>
				<GWPMultiSelect
					id="EntertainerActFormats"
					options={contract.taxonomy.actFormats}
					value={contract.taxonomy.actFormats.filter((option) => formats.includes(option.value))}
					menuIsOpen={openMultiSelect === 'act-formats'}
					onMenuOpen={() => setOpenMultiSelect('act-formats')}
					onMenuClose={() => setOpenMultiSelect((current) => (current === 'act-formats' ? null : current))}
					isOptionDisabled={(option) =>
						formats.length >= contract.constraints.actFormats.maxSelections &&
						!formats.includes(option.value)
					}
					onValueChange={(options, action) => {
						if (action.action === 'select-option' || action.action === 'deselect-option') {
							setOpenMultiSelect('act-formats');
						}
						update(
							'EntertainerActFormats',
							options.map((option) => option.value),
						);
						clearErrors('EntertainerGenres');
					}}
					placeholder="Search act formats…"
				/>
			</GWPField>
			<div className="entertainer-grid two-columns">
				<GWPField
					label="Musical genres"
					required={genresRequired}
					optional={!genresRequired}
					hint={`Up to ${contract.constraints.genres.maxSelections}; required for live music`}
					error={fieldError(errors, 'EntertainerGenres')}
				>
					<GWPMultiSelect
						id="EntertainerGenres"
						options={contract.taxonomy.genres}
						value={contract.taxonomy.genres.filter((option) => genres.includes(option.value))}
						menuIsOpen={openMultiSelect === 'genres'}
						onMenuOpen={() => setOpenMultiSelect('genres')}
						onMenuClose={() => setOpenMultiSelect((current) => (current === 'genres' ? null : current))}
						isOptionDisabled={(option) =>
							genres.length >= contract.constraints.genres.maxSelections && !genres.includes(option.value)
						}
						onValueChange={(options, action) => {
							if (action.action === 'select-option' || action.action === 'deselect-option') {
								setOpenMultiSelect('genres');
							}
							update(
								'EntertainerGenres',
								options.map((option) => option.value),
							);
						}}
						placeholder="Search genres…"
					/>
				</GWPField>
				<GWPField
					label="Specialties"
					optional
					hint={`Choose up to ${contract.constraints.specialties.maxSelections} distinctive skills or themes`}
					error={fieldError(errors, 'EntertainerSpecialties')}
				>
					<GWPMultiSelect
						id="EntertainerSpecialties"
						options={contract.taxonomy.specialties}
						value={contract.taxonomy.specialties.filter((option) => specialties.includes(option.value))}
						menuIsOpen={openMultiSelect === 'specialties'}
						onMenuOpen={() => setOpenMultiSelect('specialties')}
						onMenuClose={() =>
							setOpenMultiSelect((current) => (current === 'specialties' ? null : current))
						}
						isOptionDisabled={(option) =>
							specialties.length >= contract.constraints.specialties.maxSelections &&
							!specialties.includes(option.value)
						}
						onValueChange={(options, action) => {
							if (action.action === 'select-option' || action.action === 'deselect-option') {
								setOpenMultiSelect('specialties');
							}
							update(
								'EntertainerSpecialties',
								options.map((option) => option.value),
							);
						}}
						placeholder="Search specialties…"
					/>
				</GWPField>
			</div>
		</ApplicationSection>
	);
}
